import { DEFAULT_THRESHOLDS, type AttentionMode, type AttentionThresholds } from 'eyecontact'

export interface Settings {
  mode: AttentionMode
  graceMs: number
  resumeMs: number
  fps: number
  showMesh: boolean
  pauseWhenTabHidden: boolean
  pauseWhenWindowBlurred: boolean
  thresholds: AttentionThresholds
}

export const DEFAULT_SETTINGS: Settings = {
  mode: 'gaze',
  graceMs: 3000,
  resumeMs: 400,
  fps: 12,
  showMesh: true,
  pauseWhenTabHidden: true,
  pauseWhenWindowBlurred: true,
  thresholds: { ...DEFAULT_THRESHOLDS },
}

/**
 * Version the key rather than the payload.
 *
 * Tuning is per-face and per-room — someone who has found numbers that suit
 * their glasses and their lighting should not lose them to a reload. But the
 * meaning of a threshold can change between builds, and silently restoring a
 * value tuned against different code is worse than starting fresh. Bumping the
 * suffix retires old settings cleanly instead of trying to migrate them.
 */
const KEY = 'eyecontact.settings.v2'

/** Bounds match the sliders, and exist to survive a hand-edited localStorage. */
const NUMERIC_BOUNDS: Record<string, [number, number]> = {
  graceMs: [500, 10000],
  resumeMs: [0, 2000],
  fps: [4, 30],
  yawToleranceDeg: [5, 60],
  pitchToleranceDeg: [5, 50],
  presenceYawToleranceDeg: [5, 80],
  presencePitchToleranceDeg: [5, 70],
  gazeTolerance: [0.1, 0.9],
  blinkThreshold: [0.2, 0.95],
}

function num(key: string, value: unknown, fallback: number): number {
  if (typeof value !== 'number' || !Number.isFinite(value)) return fallback
  const bounds = NUMERIC_BOUNDS[key]
  if (!bounds) return value
  return Math.min(bounds[1], Math.max(bounds[0], value))
}

/**
 * Rebuild a Settings from whatever was in storage, field by field.
 *
 * Never spread stored data over the defaults wholesale: the stored object is
 * from an older build of this app, and a key that has since been removed would
 * ride along forever, while a key added since would arrive as undefined and
 * hand NaN to a slider. Reading each field explicitly means a new setting picks
 * up its default and a retired one is dropped.
 */
function reconcile(raw: unknown): Settings {
  if (!raw || typeof raw !== 'object') return { ...DEFAULT_SETTINGS }
  const s = raw as Record<string, unknown>
  const t = (s.thresholds ?? {}) as Record<string, unknown>
  const d = DEFAULT_SETTINGS

  const threshold = (k: keyof AttentionThresholds) => num(k, t[k], d.thresholds[k])

  return {
    mode: s.mode === 'presence' || s.mode === 'gaze' ? s.mode : d.mode,
    graceMs: num('graceMs', s.graceMs, d.graceMs),
    resumeMs: num('resumeMs', s.resumeMs, d.resumeMs),
    fps: num('fps', s.fps, d.fps),
    showMesh: typeof s.showMesh === 'boolean' ? s.showMesh : d.showMesh,
    pauseWhenTabHidden:
      typeof s.pauseWhenTabHidden === 'boolean' ? s.pauseWhenTabHidden : d.pauseWhenTabHidden,
    pauseWhenWindowBlurred:
      typeof s.pauseWhenWindowBlurred === 'boolean'
        ? s.pauseWhenWindowBlurred
        : d.pauseWhenWindowBlurred,
    thresholds: {
      yawToleranceDeg: threshold('yawToleranceDeg'),
      pitchToleranceDeg: threshold('pitchToleranceDeg'),
      presenceYawToleranceDeg: threshold('presenceYawToleranceDeg'),
      presencePitchToleranceDeg: threshold('presencePitchToleranceDeg'),
      gazeTolerance: threshold('gazeTolerance'),
      blinkThreshold: threshold('blinkThreshold'),
    },
  }
}

/**
 * Every access is wrapped: Safari in private mode throws on the localStorage
 * getter itself, not merely on read, and a page with site data blocked throws
 * on write. Tuning is a convenience — it must never take the app down with it.
 */
export function loadSettings(): Settings {
  try {
    const stored = window.localStorage.getItem(KEY)
    if (!stored) return { ...DEFAULT_SETTINGS }
    return reconcile(JSON.parse(stored))
  } catch {
    return { ...DEFAULT_SETTINGS }
  }
}

export function saveSettings(settings: Settings): void {
  try {
    window.localStorage.setItem(KEY, JSON.stringify(settings))
  } catch {
    /* storage full, blocked, or private mode: the app works fine without it */
  }
}

export function clearSettings(): void {
  try {
    window.localStorage.removeItem(KEY)
  } catch {
    /* see saveSettings */
  }
}

/** True when the current settings differ from the shipped defaults. */
export function isCustomised(s: Settings): boolean {
  return JSON.stringify(s) !== JSON.stringify(DEFAULT_SETTINGS)
}
